/**
 *  
 */
class InputState {
  active = false;
  started = false;
  repeating = false;
  index = -1;

  constructor(index) {
    this.index = index;
  }
}

/**
 * Makes continuous gamepad input usable with turn-based movement, providing an API roughly similar to the
 * web platform standard input events for keyup, keydown
 */
class Gamepad extends EventTarget {
  // TODO: configurable sensitivity
  buttonThreshold = 0.5;
  joystickThreshold = 0.1;
  initialRepeat = 375;
  repeatInterval = 90;

  registerInstance(index) {
    // TODO: handle multiple gamepads
    this.gamepadIndex = index;
    this.lastSnapshot = this.getSnapshot();
  }

  isConnected() {
    return this.gamepadIndex !== null;
  }

  getSnapshot() {
    const gamepad = navigator.getGamepads()[this.gamepadIndex];

    if (gamepad == null) return;

    return {
      axes: gamepad.axes,
      buttons: gamepad.buttons,
      timestamp: gamepad.timestamp
    }
  }

  disconnect() {
    this.gamepadIndex = null;
  }

  static StickUp = 0;
  static StickDown = 1;
  static StickLeft = 2;
  static StickRight = 3;

  stickDirection(num) {
    switch(num) {
      case Gamepad.StickUp: return "StickUp";
      case Gamepad.StickDown: return "StickDown";
      case Gamepad.StickLeft: return "StickLeft";
      case Gamepad.StickRight: return "StickRight";
    }
  }

  mapOrthogonalAxes(horizontal, vertical) {
    if (Math.abs(horizontal) > 0.75) {
      return Math.sign(horizontal) > 0 ? Gamepad.StickRight : Gamepad.StickLeft;
    } else if (Math.abs(vertical) > 0.75) {
      return Math.sign(vertical) > 0 ? Gamepad.StickDown : Gamepad.StickUp;
    }
    return null;
  }

  leftStickState = null;
  leftStickStarted = null;
  leftStickRepeating = false;

  state = {
    ButtonA: new InputState(0),
    ButtonB: new InputState(1),
    ButtonX: new InputState(2),
    ButtonY: new InputState(3),
    ButtonLB: new InputState(4),
    ButtonRB: new InputState(5),
    DpadUp: new InputState(12),
    DpadDown: new InputState(13),
    DpadLeft: new InputState(14),
    DpadRight: new InputState(15)
  }

  mapButton(code, snapshot) {
    const button = snapshot.buttons[this.state[code].index];
    
    if (this.state[code].active && !button.pressed) {
      this.state[code].active = false;
      this.state[code].repeating = false;

    } else if (button.pressed) {
      if (this.state[code].active) {
        const threshold = this.state[code].repeating ? this.repeatInterval : this.initialRepeat;
        const timestamp = performance.now();

        if ((timestamp - this.state[code].started) > threshold) {
          this.dispatchEvent(new CustomEvent("buttonpressed", {
            detail: { code }
          }));
          this.state[code].started = timestamp;
          this.state[code].repeating = true;
        }
      } else {
        this.dispatchEvent(new CustomEvent("buttonpressed", {
          detail: { code }
        }));
        this.state[code].active = true;
        this.state[code].started = performance.now();
      }
    }
  }

  poll() {
    const snapshot = this.getSnapshot();

    if (!snapshot) {
      this.disconnect();
      return;
    }

    // Coalesce button presses
    this.mapButton("ButtonA", snapshot);
    this.mapButton("ButtonB", snapshot);
    this.mapButton("ButtonX", snapshot);
    this.mapButton("ButtonY", snapshot);
    this.mapButton("ButtonLB", snapshot);
    this.mapButton("ButtonRB", snapshot);
    this.mapButton("DpadUp", snapshot);
    this.mapButton("DpadDown", snapshot);
    this.mapButton("DpadLeft", snapshot);
    this.mapButton("DpadRight", snapshot);

    // Coalesce left stick axes
    // TODO: refactor this to use the state structure consistently
    const leftStickH = snapshot.axes[0];
    const leftStickV = snapshot.axes[1];
    const leftStick = this.mapOrthogonalAxes(leftStickH, leftStickV);

    if (leftStick == null && this.leftStickState != null) {
      this.dispatchEvent(new CustomEvent("stickreleased", {
        detail: {
          code: this.stickDirection(leftStick)
        }
      }));
      this.leftStickState = null;
      this.leftStickRepeating = false;
    } else if (leftStick != null) {
      if (leftStick == this.leftStickState) {
        const threshold = this.leftStickRepeating ? this.repeatInterval : this.initialRepeat;
        const timestamp = performance.now();
        if ((timestamp - this.leftStickStarted) > threshold) {
          this.dispatchEvent(new CustomEvent("stickpressed", {
            detail: {
              code: this.stickDirection(leftStick)
            }
          }));
          this.leftStickStarted = timestamp;
          this.leftStickRepeating = true;
        }
      } else {
        this.dispatchEvent(new CustomEvent("stickpressed", {
          detail: {
            code: this.stickDirection(leftStick)
          }
        }));

        this.leftStickState = leftStick;
        this.leftStickStarted = performance.now();
      }
    }
  }
}

export default Gamepad;